/**
 * Toast Context - Global toast notification system
 * 
 * Usage:
 *   import { useToast } from '../Context/ToastContext';
 *   
 *   const { showToast } = useToast();
 *   showToast('Operation successful!', 'success');
 *   showToast('Something went wrong', 'error');
 *   showToast('Please check your input', 'warning');
 *   showToast('New message received', 'info');
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';

// ============================================
// ANIMATIONS
// ============================================

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const progressShrink = keyframes`
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const ToastContainerWrapper = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 99999;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 400px;
  pointer-events: none;
`;

const getToastColors = (type) => {
  switch (type) {
    case 'success':
      return {
        bg: 'linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%)',
        border: 'rgba(16, 185, 129, 0.5)',
        icon: '✓',
        iconBg: 'rgba(255, 255, 255, 0.2)'
      };
    case 'error':
      return {
        bg: 'linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(185, 28, 28, 0.95) 100%)',
        border: 'rgba(239, 68, 68, 0.5)',
        icon: '✕',
        iconBg: 'rgba(255, 255, 255, 0.2)'
      };
    case 'warning':
      return {
        bg: 'linear-gradient(135deg, rgba(245, 158, 11, 0.95) 0%, rgba(217, 119, 6, 0.95) 100%)',
        border: 'rgba(245, 158, 11, 0.5)',
        icon: '⚠',
        iconBg: 'rgba(255, 255, 255, 0.2)'
      };
    case 'info':
    default:
      return {
        bg: 'linear-gradient(135deg, rgba(99, 102, 241, 0.95) 0%, rgba(79, 70, 229, 0.95) 100%)',
        border: 'rgba(99, 102, 241, 0.5)',
        icon: 'ℹ',
        iconBg: 'rgba(255, 255, 255, 0.2)'
      };
  }
};

const ToastItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  background: ${props => getToastColors(props.$type).bg};
  border: 1px solid ${props => getToastColors(props.$type).border};
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  backdrop-filter: blur(10px);
  color: white;
  pointer-events: auto;
  position: relative;
  overflow: hidden;
  min-width: 300px;
  
  animation: ${props => props.$isExiting 
    ? css`${slideOut} 0.3s ease-in forwards` 
    : css`${slideIn} 0.3s ease-out`};
`;

const IconWrapper = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${props => getToastColors(props.$type).iconBg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
`;

const ContentWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const ToastTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: ${props => props.$hasMessage ? '4px' : '0'};
  line-height: 1.4;
`;

const ToastMessage = styled.div`
  font-size: 13px;
  opacity: 0.9;
  line-height: 1.4;
  word-wrap: break-word;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.4);
  animation: ${progressShrink} ${props => props.$duration}ms linear forwards;
`;

// ============================================
// CONTEXT
// ============================================

const ToastContext = createContext();

let toastIdCounter = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(toast => 
      toast.id === id ? { ...toast, isExiting: true } : toast
    ));
    
    // Actually remove after animation
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 300);
  }, []);

  const showToast = useCallback((message, type = 'info', options = {}) => {
    const {
      title,
      duration = 5000,
      persistent = false
    } = options;

    const id = ++toastIdCounter;
    
    const newToast = {
      id,
      message,
      type,
      title: title || getDefaultTitle(type),
      duration,
      persistent,
      isExiting: false
    };

    setToasts(prev => [...prev, newToast]);

    // Auto-remove after duration (unless persistent)
    if (!persistent && duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, [removeToast]);

  // Convenience methods
  const success = useCallback((message, options) => showToast(message, 'success', options), [showToast]);
  const error = useCallback((message, options) => showToast(message, 'error', options), [showToast]);
  const warning = useCallback((message, options) => showToast(message, 'warning', options), [showToast]);
  const info = useCallback((message, options) => showToast(message, 'info', options), [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, success, error, warning, info, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </ToastContext.Provider>
  );
};

// ============================================
// TOAST CONTAINER COMPONENT
// ============================================

const ToastContainer = ({ toasts, onClose }) => {
  if (toasts.length === 0) return null;

  return (
    <ToastContainerWrapper>
      {toasts.map(toast => (
        <ToastItem 
          key={toast.id} 
          $type={toast.type}
          $isExiting={toast.isExiting}
        >
          <IconWrapper $type={toast.type}>
            {getToastColors(toast.type).icon}
          </IconWrapper>
          
          <ContentWrapper>
            <ToastTitle $hasMessage={!!toast.message && toast.message !== toast.title}>
              {toast.title}
            </ToastTitle>
            {toast.message && toast.message !== toast.title && (
              <ToastMessage>{toast.message}</ToastMessage>
            )}
          </ContentWrapper>
          
          <CloseButton onClick={() => onClose(toast.id)}>
            ×
          </CloseButton>
          
          {!toast.persistent && (
            <ProgressBar $duration={toast.duration} />
          )}
        </ToastItem>
      ))}
    </ToastContainerWrapper>
  );
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const getDefaultTitle = (type) => {
  switch (type) {
    case 'success': return 'Success';
    case 'error': return 'Error';
    case 'warning': return 'Warning';
    case 'info': 
    default: return 'Info';
  }
};

// ============================================
// HOOK
// ============================================

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;

